import {
  AccentIcon,
  AccentIconShapeEnum,
  AccentVariantEnum,
} from '@delmonicos/shared-design-system-react/src/components/accent-icon/accent-icon';
import { ButtonIcon } from '@delmonicos/shared-design-system-react/src/components/button-icon/button-icon';
import {
  Dialog,
  DialogSize,
} from '@delmonicos/shared-design-system-react/src/components/dialog/dialog';
import {
  Divider,
  DividerOrientation,
} from '@delmonicos/shared-design-system-react/src/components/divider/divider';
import { withProtectedResource } from '@delmonicos/shared-design-system-react/src/components/protected-resources/with-protected-resource.helper';
import { Tooltip } from '@delmonicos/shared-design-system-react/src/components/tooltip/tooltip';
import { GatewayChargePointModel } from '@delmonicos/shared-sdk/libs/cpms/src/apps/charge-point-operators-gateway/src/charge-points/models/outputs/gateway-charge-point.model';
import {
  hasAccessTo,
  safeHasAccessTo,
} from '@delmonicos/shared-sdk/libs/cpms/src/clients/utils/helper/has-access-to';
import { ActionsByResource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/action.enum';
import { Resource } from '@delmonicos/shared-sdk/libs/cpms/src/libs/identity-service-lib/src/wrapper/enums/resource.enum';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { generatePath, useParams } from 'react-router-dom';

import { APP_PATH } from '@app/config/app-paths';
import { permissions } from '@app/core/authentication/authentication.slice';
import { useAppSelector } from '@app/core/hooks';
import { ChargePointConfigurationController } from '@app/feature/charge-point/charge-point-configuration/charge-point-configuration.controller';
import { useCpmsBoGetChargePointQuery } from '@service/cpms-bo-charge-point.service';
import { useSetPageActionContext } from '@shared/layout/page-layout/page-layout-action.context-provider';
import { useSetPageBreadcrumbContext } from '@shared/layout/page-layout/page-layout-breadcrumb.context-provider';
import { useSetPageTitleContext } from '@shared/layout/page-layout/page-layout-title.context-provider';

import { ChargePointUpdateController } from '../charge-point-update/charge-point-update.controller';
import { ChargePointContextProvider } from '../charge-point.context';
import { ChargePointDetailsView } from './charge-point-details.view';

interface ChargePointDetailsControllerProps {
  resource: GatewayChargePointModel;
}

const ChargePointDetailsController = (
  props: ChargePointDetailsControllerProps,
): React.JSX.Element => {
  // CONTEXT ///////////////////////////////////////////////////////////////////

  const { resource } = props;
  const { t } = useTranslation();

  // USER PERMISSIONS //////////////////////////////////////////////////////////

  const userPermissions = useAppSelector(permissions);

  // ACTIONS ///////////////////////////////////////////////////////////////////

  const [actionUpdateIsDialogOpen, setActionUpdateIsDialogOpen] =
    useState(false);

  const [actionConfigurationIsDialogOpen, setActionConfigurationIsDialogOpen] =
    useState(false);

  // PAGE TITLE ////////////////////////////////////////////////////////////////

  const setPageTitle = useSetPageTitleContext();

  useEffect(() => {
    setPageTitle(resource.name);
  }, [resource, setPageTitle]);

  // PAGE ACTIONS //////////////////////////////////////////////////////////////

  const setPageAction = useSetPageActionContext();

  useEffect(() => {
    const actions = [];

    // add isOnline status icon in page action if charge point is offline
    if (!resource.isOnline) {
      actions.push(
        <Tooltip
          key="is-online"
          content={t(
            'delmonicos-shared-sdk.outputs-charge-point-is-online.FALSE',
          )}>
          <AccentIcon
            icon="wifi-off"
            variant={AccentVariantEnum.Disconnected}
            shape={AccentIconShapeEnum.Round}
          />
        </Tooltip>,
      );
    }

    actions.push(
      resource.isLocked ? (
        <Tooltip
          key="is-not-locked"
          content={t(
            'delmonicos-shared-sdk.outputs-charge-point-is-locked.TRUE',
          )}>
          <AccentIcon
            icon="terminal-maintenance"
            shape={AccentIconShapeEnum.Round}
            variant={AccentVariantEnum.Locked}
          />
        </Tooltip>
      ) : (
        <Tooltip
          key="is-locked"
          content={t(
            'delmonicos-shared-sdk.outputs-charge-point-is-locked.FALSE',
          )}>
          <AccentIcon
            icon="electric-terminal"
            shape={AccentIconShapeEnum.Round}
            variant={AccentVariantEnum.Available}
          />
        </Tooltip>
      ),
    );

    actions.push(
      <Divider key="divider" orientation={DividerOrientation.Vertical} />,
    );

    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.CHARGE_POINTS,
        action: ActionsByResource.CHARGE_POINTS.READ,
      })
    ) {
      actions.push(
        <ButtonIcon
          icon="settings-01"
          onClick={() => setActionConfigurationIsDialogOpen(true)}
          key="configuration"
        />,
      );
    }

    if (
      userPermissions &&
      hasAccessTo(userPermissions, {
        resourceName: Resource.CHARGE_POINTS,
        action: ActionsByResource.CHARGE_POINTS.UPDATE,
      })
    ) {
      actions.push(
        <ButtonIcon
          onClick={() => setActionUpdateIsDialogOpen(true)}
          icon="edit-05"
          key="update"
        />,
      );
    }

    setPageAction(actions);
  }, [setPageAction, userPermissions, resource.isOnline, resource.isLocked, t]);

  // PAGE BREADCRUMB ///////////////////////////////////////////////////////////

  const setPageBreadcrumb = useSetPageBreadcrumbContext();

  useEffect(() => {
    if (
      !resource?.network?.id ||
      !resource?.network?.name ||
      !resource?.location?.id ||
      !resource?.location?.name
    ) {
      return;
    }

    setPageBreadcrumb([
      {
        label: resource.network.name,
        url: generatePath(APP_PATH.NETWORK.DETAILS, {
          id: resource.network.id,
        }),
        iconName: 'server-06',
      },
      {
        label: resource.location.name,
        url: generatePath(APP_PATH.LOCATION.DETAILS, {
          id: resource.location.id,
        }),
        iconName: 'map-01',
      },
    ]);
  }, [setPageBreadcrumb, resource, t]);

  // HANDLERS //////////////////////////////////////////////////////////////////

  // Nothing to do

  // RENDER ////////////////////////////////////////////////////////////////////

  return (
    <ChargePointContextProvider data={resource}>
      <ChargePointDetailsView />
      <Dialog
        onClose={() => setActionConfigurationIsDialogOpen(false)}
        open={actionConfigurationIsDialogOpen}
        height="100%"
        size={DialogSize.LARGE}>
        <Dialog.Header
          icon="settings-01"
          onClose={() => setActionConfigurationIsDialogOpen(false)}
          title={t('$.charge-point-configuration.title')}
        />
        <ChargePointConfigurationController />
      </Dialog>
      <Dialog
        onClose={() => setActionUpdateIsDialogOpen(false)}
        open={actionUpdateIsDialogOpen}
        confirmClose
        height="100%"
        size={DialogSize.LARGE}>
        <Dialog.Header
          icon="electric-terminal"
          onClose={() => setActionUpdateIsDialogOpen(false)}
          title={t('$.charge-point-update.title')}
        />
        <ChargePointUpdateController
          onCancel={() => setActionUpdateIsDialogOpen(false)}
          onSuccess={() => setActionUpdateIsDialogOpen(false)}
        />
      </Dialog>
    </ChargePointContextProvider>
  );
};

export const ChargePointDetailsPage = (): React.JSX.Element => {
  const userPermissions = useAppSelector(permissions);
  const params = useParams();
  const queryState = useCpmsBoGetChargePointQuery(params.id ?? '');

  const { Component } = withProtectedResource<
    GatewayChargePointModel,
    {},
    GatewayChargePointModel
  >(ChargePointDetailsController, queryState, {
    isAuthorized: () =>
      safeHasAccessTo(userPermissions, {
        resourceName: Resource.CHARGE_POINTS,
        action: ActionsByResource.CHARGE_POINTS.READ,
      }),
  })({});

  return Component;
};
